import type {
  AdminProductionProcessesResponse,
  AdminProductionProcessResponse,
  UpdateAdminProductionProcessBody,
} from '@/api/(admin)/production-processes/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/**
 * Every call goes to `/api/admin/production-processes`, so each takes
 * `API_ROOT_URL`; `apiFetch`'s default base would put `/api-service` in front
 * of the path.
 *
 * Named `…Admin…` throughout to keep them apart from the customer-facing
 * `/v3/production-processes` list that feeds the dropdowns.
 */

/** The list. Takes a pre-built query string; paging lives in the hook. */
export async function fetchAdminProductionProcesses(
  qs: string,
): Promise<AdminProductionProcessesResponse> {
  return apiFetch<AdminProductionProcessesResponse>(
    `/api/admin/production-processes?${qs}`,
    undefined,
    API_ROOT_URL,
  );
}

/**
 * One record. The only endpoint that returns `translations`. Answers the
 * `{ data, metadata }` envelope; the hook unwraps it.
 */
export async function fetchAdminProductionProcess(
  id: string,
): Promise<AdminProductionProcessResponse> {
  return apiFetch<AdminProductionProcessResponse>(
    `/api/admin/production-processes/${encodeURIComponent(id)}`,
    undefined,
    API_ROOT_URL,
  );
}

/**
 * Saves an edit. A PATCH, gated behind `admin-production-processes:update`.
 * Migrated to `/api/admin/production-processes/:id`, so it takes
 * `API_ROOT_URL`. Answers the `{ data, metadata }` envelope; nothing here
 * reads it — the hook refetches.
 */
export async function updateAdminProductionProcess(
  id: string,
  body: UpdateAdminProductionProcessBody,
): Promise<AdminProductionProcessResponse> {
  return apiFetch<AdminProductionProcessResponse>(
    `/api/admin/production-processes/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * The dropdown list: every production process, unpaginated, in the list's
 * row shape. `isDropDown=true` ignores `page` and `count` and answers the
 * same envelope with one page holding every row — what the old dashboard's
 * facility-processes page used for its production-process picker too.
 */
export async function fetchAdminProductionProcessOptions(): Promise<AdminProductionProcessesResponse> {
  return apiFetch<AdminProductionProcessesResponse>(
    '/api/admin/production-processes?isDropDown=true',
    undefined,
    API_ROOT_URL,
  );
}
